import { buildKmEntityPath } from '@/entities/ontology';

/** Имена query-параметров отбора раздела сущностей */
export const KM_ENTITIES_QUERY = {
  domain: 'domain',
  type: 'type',
  related: 'related',
  search: 'search',
} as const;

/** Условие отбора, которое меняет пользователь */
export type KmEntitiesFilterKey = keyof typeof KM_ENTITIES_QUERY;

/**
 * Отбор раздела сущностей, прочитанный из адреса страницы. Условия сочетаются между собой,
 * а значения одного условия — как «любое из них»; пустой набор условия не накладывает.
 */
export type KmEntitiesFilter = {
  /** Коды доменов */
  domain: string[];
  /** Коды типов сущностей */
  type: string[];
  /** Идентификаторы сущностей, связь с которыми отбирает выдачу */
  related: number[];
  /** Строка поиска по значениям атрибутов */
  search: string;
};

/**
 * Отбор из адреса страницы. Нечисловой идентификатор связанной сущности отбрасывается:
 * адрес правится и руками, а такое значение бэкенд отвергает ошибкой.
 */
export const parseKmEntitiesFilter = (searchParams: URLSearchParams): KmEntitiesFilter => ({
  domain: searchParams.getAll(KM_ENTITIES_QUERY.domain).filter(Boolean),
  type: searchParams.getAll(KM_ENTITIES_QUERY.type).filter(Boolean),
  related: searchParams
    .getAll(KM_ENTITIES_QUERY.related)
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0),
  search: searchParams.get(KM_ENTITIES_QUERY.search) ?? '',
});

/**
 * Адрес страницы с изменённым условием отбора: каждое значение занимает свой повтор
 * параметра, пустой набор параметр убирает. Прочие параметры адреса сохраняются.
 */
export const applyKmEntitiesFilter = (
  searchParams: URLSearchParams,
  key: KmEntitiesFilterKey,
  value: string | string[],
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  const values = (Array.isArray(value) ? value : [value]).filter(Boolean);

  next.delete(KM_ENTITIES_QUERY[key]);
  values.forEach((item) => next.append(KM_ENTITIES_QUERY[key], item));

  return next;
};

/**
 * Адрес списка раздела с заданным отбором: карточка закрывается — сущность, по связям
 * которой идёт отбор, в выдачу не входит, и выделять в списке нечего.
 */
export const buildKmEntitiesListLocation = (searchParams: URLSearchParams) => ({
  pathname: buildKmEntityPath(),
  search: searchParams.toString(),
});

/**
 * Адрес карточки сущности с сохранением текущего отбора.
 */
export const buildKmEntityLocation = (id: number, searchParams: URLSearchParams) => ({
  pathname: buildKmEntityPath(id),
  search: searchParams.toString(),
});
